import type { CommonJourneyItemFormData } from './useJourneyItemForm';
import { SpeechBubblePreview } from '../SpeechBubblePreview';

interface JourneyItemFormFieldsProps {
    formData: CommonJourneyItemFormData;
    itemType: string;
    disableProgressType?: boolean;
    onChange: (field: string, value: string | number | boolean) => void;
}

export const JourneyItemFormFields = ({
    formData,
    itemType,
    disableProgressType = false,
    onChange,
}: JourneyItemFormFieldsProps) => {
    return (
        <>
            {/* Item Type - Display Only */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Item Type</label>
                <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700 capitalize">
                    {itemType.replace('-', ' ')}
                </div>
            </div>

            {/* Title */}
            <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                </label>
                <input
                    type="text"
                    id="title"
                    value={formData.title}
                    onChange={(e) => onChange('title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter item title"
                    required
                />
            </div>

            {/* Description */}
            <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                </label>
                <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => onChange('description', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter item description"
                    rows={3}
                    required
                />
            </div>

            {/* Ollie Text */}
            <div>
                <label htmlFor="ollieText" className="block text-sm font-medium text-gray-700 mb-1">
                    Ollie Text
                </label>
                <textarea
                    id="ollieText"
                    value={formData.ollieText}
                    onChange={(e) => onChange('ollieText', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter Ollie text"
                    rows={3}
                />
                {formData.ollieText && (
                    <div className="mt-2">
                        <span className="block text-xs text-gray-500 mb-1">Phone preview</span>
                        <div className="w-[375px] max-w-full mx-auto rounded-xl border border-gray-200 bg-osGrey-5 p-4 flex justify-center">
                            <SpeechBubblePreview text={formData.ollieText} />
                        </div>
                    </div>
                )}
            </div>

            {/* Text Templating Prompt */}
            <div>
                <label htmlFor="textTemplatingPrompt" className="block text-sm font-medium text-gray-700 mb-1">
                    Text Templating Prompt
                </label>
                <textarea
                    id="textTemplatingPrompt"
                    value={formData.textTemplatingPrompt}
                    onChange={(e) => onChange('textTemplatingPrompt', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter text templating prompt"
                    rows={3}
                />
            </div>

            {/* Brain Points & Energy Cost */}
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label htmlFor="brainPoints" className="block text-sm font-medium text-gray-700 mb-1">
                        Brain Points *
                    </label>
                    <input
                        type="number"
                        id="brainPoints"
                        value={formData.brainPoints}
                        onChange={(e) => onChange('brainPoints', parseInt(e.target.value, 10) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        min={0}
                        required
                    />
                </div>
                <div>
                    <label htmlFor="energyCost" className="block text-sm font-medium text-gray-700 mb-1">
                        Energy Cost *
                    </label>
                    <input
                        type="number"
                        id="energyCost"
                        value={formData.energyCost}
                        onChange={(e) => onChange('energyCost', parseInt(e.target.value, 10) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        min={0}
                        required
                    />
                </div>
            </div>

            {/* Progress Type */}
            <div>
                <label htmlFor="progressType" className="block text-sm font-medium text-gray-700 mb-1">
                    Progress Type
                </label>
                <select
                    id="progressType"
                    value={formData.progressType}
                    onChange={(e) => onChange('progressType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={disableProgressType}
                >
                    <option value="fixed">Fixed</option>
                    <option value="undetermined">Undetermined</option>
                </select>
            </div>

            {/* Max Progress (only when fixed) */}
            {formData.progressType === 'fixed' && (
                <div>
                    <label htmlFor="maxProgress" className="block text-sm font-medium text-gray-700 mb-1">
                        Max Progress
                    </label>
                    <input
                        type="number"
                        id="maxProgress"
                        value={formData.maxProgress ?? 1}
                        onChange={(e) => onChange('maxProgress', parseInt(e.target.value, 10) || 1)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        min={1}
                    />
                </div>
            )}
        </>
    );
};
